// src/pages/Home.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Row, Col, Button, Form, Carousel } from 'react-bootstrap';
import AdCard from '../components/AdCard';
import FilterSidebar from '../components/FilterSidebar';
import { FaSpinner, FaMapMarkerAlt } from 'react-icons/fa';
import { getCurrentLocation, getNearbyAds } from '../services/locationService';
import Map from '../components/Map';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import './Home.css';

const categoryBanners = [
  {
    key: 'welcome',
    src: '/banners/welcome-banner.png',
    alt: 'Welcome banner'
  },
  {
    key: 'chickens',
    src: '/banners/chickens-banner.png',
    alt: 'Chickens banner'
  },
  {
    key: 'cattle',
    src: '/banners/cattle-banner.png',
    alt: 'Cattle banner'
  },
  {
    key: 'pets',
    src: '/banners/pets-banner.png',
    alt: 'Pets banner'
  },
  {
    key: 'sheep-goat',
    src: '/banners/sheep-banner.png',
    alt: 'Sheep/Goat banner'
  }
];

const Home = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters] = useState({
    category: '',
    minPrice: '',
    maxPrice: '',
    state: '',
    city: '',
    condition: ''
  });
  const [userLocation, setUserLocation] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showNearbyOnly, setShowNearbyOnly] = useState(false);
  const [searchRadius, setSearchRadius] = useState(10); // Default 10km radius
  const navigate = useNavigate();

  // Request location permission when component mounts
  useEffect(() => {
    const requestLocationPermission = async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          if (result.state === 'prompt') {
            // Request location permission
            getLocation();
          }
        } else {
          // Fallback for browsers that don't support permissions API
          getLocation();
        }
      } catch (error) {
        console.error('Error requesting location permission:', error);
      }
    };

    requestLocationPermission();
  }, []);

  const getLocation = async () => {
    setIsGettingLocation(true);
    try {
      const position = await getCurrentLocation();
      setUserLocation(position);
      setShowNearbyOnly(true);
    } catch (error) {
      console.error('Error getting location:', error);
      setError('Failed to get your location. Please try again.');
    } finally {
      setIsGettingLocation(false);
    }
  };

  useEffect(() => {
    const fetchAds = async () => {
      try {
        if (showNearbyOnly && userLocation) {
          const nearbyAds = await getNearbyAds(userLocation.latitude, userLocation.longitude, searchRadius); // Use selected radius
          setAds(nearbyAds);
        } else {
          const response = await axios.get(API_ENDPOINTS.ADS.GET_ALL, { 
            params: filters 
          });
          setAds(response.data);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching ads:', error);
        setError('Failed to load ads. Please try again later.');
        setLoading(false);
      }
    };

    fetchAds();
  }, [filters, showNearbyOnly, userLocation, searchRadius]);

  const handleFilterApply = (newFilters) => {
    // Build query string from filters
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        params.append(key, value);
      }
    });
    navigate(`/search?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <FaSpinner className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <Container fluid>
        {/* Banners Carousel */}
        <Carousel
          interval={3500}
          controls={true}
          indicators={true}
          style={{ 
            width: '100vw', 
            marginBottom: 0, 
            marginTop: -70,
            marginLeft: 'calc(-50vw + 50%)',
            marginRight: 'calc(-50vw + 50%)'
          }}
        >
          {categoryBanners.map(banner => (
            <Carousel.Item key={banner.key}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                width: '100%',
                height: 250,
                backgroundColor: banner.key === 'chickens' ? '#fff' : undefined,
                padding: '0 20px',
                borderRadius: '16px',
                overflow: 'hidden'
              }}>
                <img
                  src={banner.src}
                  alt={banner.alt}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '16px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
        <Row>
          {/* Filter Sidebar */}
          <Col md={3}>
            <FilterSidebar onFilterApply={handleFilterApply} />
            <div className="mt-3">
              <Button
                variant={showNearbyOnly ? "primary" : "outline-primary"}
                onClick={getLocation}
                disabled={isGettingLocation}
                className="w-100 d-flex align-items-center justify-content-center gap-2"
              >
                <FaMapMarkerAlt />
                {isGettingLocation ? 'Getting Location...' : 'Show Nearby Ads'}
              </Button>
              {userLocation && showNearbyOnly && (
                <div className="mt-3">
                  <Form.Group>
                    <Form.Label>Search Radius: {searchRadius} km</Form.Label>
                    <Form.Range
                      min="1"
                      max="50"
                      value={searchRadius}
                      onChange={(e) => setSearchRadius(Number(e.target.value))}
                      className="mt-2"
                    />
                  </Form.Group>
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Showing ads within {searchRadius}km of your location
                  </p>
                </div>
              )}
            </div>
          </Col>

          {/* Listings */}
          <Col md={9}>
            <div className="home-listings-section min-vh-100" style={{ paddingTop: '1rem', paddingBottom: '2rem' }}>
              <h2 className="text-center mb-4 fw-bold text-dark">
                {showNearbyOnly ? 'Nearby Animal Listings' : 'Latest Animal Marketplace Listings'}
              </h2>
              
              {/* Map View */}
              {showNearbyOnly && userLocation && (
                <div className="mb-4">
                  <Map
                    center={[userLocation.latitude, userLocation.longitude]}
                    zoom={11}
                    markers={[
                      {
                        latitude: userLocation.latitude,
                        longitude: userLocation.longitude,
                        popup: 'Your Location'
                      },
                      ...ads.map(ad => ({
                        latitude: ad.coordinates?.latitude,
                        longitude: ad.coordinates?.longitude,
                        popup: `${ad.title} - ${ad.price}`
                      }))
                    ]}
                    height="300px"
                  />
                </div>
              )}

              {ads.length === 0 ? (
                <div className="text-center text-muted py-16">
                  {showNearbyOnly 
                    ? 'No ads available nearby. Try expanding your search area or viewing all ads.'
                    : 'No ads available at the moment'}
                </div>
              ) : (
                <Row xs={1} sm={2} md={2} lg={3} xl={3} className="ads-grid-row">
                  {ads.map((ad) => (
                    <Col key={ad._id}>
                      <AdCard ad={ad} />
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Home;
